/* Long Street Comics — ledger megamenu behavior.
   Wraps the same <details>/<summary> Dawn uses for header-menu (native
   click + Enter/Space toggle, so keyboard access works with zero JS), and
   layers on:
   - hover intent: opening/closing on a short delay so the panel doesn't
     flicker while the pointer travels from the trigger down into it
   - Escape: closes the open panel and returns focus to its trigger
   - focusout: closes once focus leaves the trigger + panel entirely
   - exclusivity: opening one panel closes any other open sibling, so a fast
     hover sweep across top-level nav items (or tabbing/clicking between
     triggers) can never leave two full-width panels open at once
   Loaded only when section.settings.menu_type_desktop == 'mega'
   (sections/header.liquid), guarded to desktop widths by the header's own
   breakpoint (the mobile drawer is a separate component and untouched). */

if (!customElements.get('lsc-mega-menu')) {
  class LscMegaMenu extends HTMLElement {
    constructor() {
      super();
      this.details = this.querySelector('details');
      this.summary = this.details ? this.details.querySelector('summary') : null;
      this.header = document.querySelector('.header-wrapper');

      this.openDelay = 80;
      this.closeDelay = 200;
      this.openTimer = null;
      this.closeTimer = null;

      this.onMouseEnter = this.onMouseEnter.bind(this);
      this.onMouseLeave = this.onMouseLeave.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onToggle = this.onToggle.bind(this);

      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('keydown', this.onKeyDown);
      this.addEventListener('focusout', this.onFocusOut);
      if (this.details) this.details.addEventListener('toggle', this.onToggle);
    }

    clearTimers() {
      window.clearTimeout(this.openTimer);
      window.clearTimeout(this.closeTimer);
    }

    onMouseEnter() {
      if (!this.details) return;
      this.clearTimers();
      if (this.details.open) return;
      this.openTimer = window.setTimeout(() => this.open(), this.openDelay);
    }

    onMouseLeave() {
      if (!this.details) return;
      this.clearTimers();
      this.closeTimer = window.setTimeout(() => this.close(), this.closeDelay);
    }

    onKeyDown(event) {
      if (event.code?.toUpperCase() !== 'ESCAPE' && event.key !== 'Escape') return;
      if (!this.details || !this.details.open) return;
      this.close();
      this.summary?.focus();
    }

    onFocusOut() {
      window.setTimeout(() => {
        if (this.details && this.details.open && !this.contains(document.activeElement)) {
          this.close();
        }
      });
    }

    onToggle() {
      if (!this.header) return;

      // Exclusive accordion: at most one megamenu panel open at a time.
      // This runs for every toggle regardless of trigger (hover-intent
      // open/close, native click, or keyboard Enter/Space), since sibling
      // <lsc-mega-menu> instances don't otherwise coordinate -- without
      // this, a fast hover sweep across the nav (or tabbing/clicking
      // between triggers) could leave two full-width panels open.
      if (this.details.open) {
        document.querySelectorAll('lsc-mega-menu').forEach((el) => {
          if (el !== this && el.details && el.details.hasAttribute('open')) {
            el.details.removeAttribute('open');
          }
        });
      }

      this.summary?.setAttribute('aria-expanded', String(this.details.open));

      // Read fresh from the DOM rather than trusting `this.details.open`
      // alone: closing this.details and opening a sibling can both fire
      // 'toggle' in either order depending on the browser, so a value
      // computed from "is anything currently open" is correct regardless
      // of which onToggle call lands last -- unlike a value derived only
      // from this instance's own open state, which a same-tick sibling
      // toggle could stomp with a stale answer.
      this.header.preventHide = !!document.querySelector('lsc-mega-menu > details[open]');

      // Matches HeaderMenu.onToggle (details-disclosure.js): the sticky
      // header's max-height calc for .mega-menu__content reads this var
      // (component-mega-menu.css) and falls back to a hardcoded 20rem
      // without it. Dawn's own header-menu element only sets this for
      // elements literally named <header-menu>, so replicate it here too.
      if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
      document.documentElement.style.setProperty(
        '--header-bottom-position-desktop',
        `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
      );
    }

    open() {
      if (this.details) this.details.setAttribute('open', '');
    }

    close() {
      if (this.details) this.details.removeAttribute('open');
    }
  }

  customElements.define('lsc-mega-menu', LscMegaMenu);
}
